import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { isNil } from 'lodash-es';
import useTimezone from '../../../hooks/useTimezone';
import { setSearchSelection } from '../../../redux/ducks/StorageDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import CategoryBox from '../CategoryBox';
import { CATEGORY_DESCRIPTORS } from './CategoriesDescriptors';

const Categories = () => {
  const { t } = useTranslation();
  const { currentTimezone } = useTimezone();
  const dispatch = useDispatch();
  const configCategories = useSelector(
    (state: RootReducerType) => state.config.appConfig?.categories,
  );
  const searchSelection = useSelector(
    (state: RootReducerType) => state.storage.searchSelection,
  );

  const searchSel = useMemo(() => {
    if (searchSelection) {
      return { ...searchSelection };
    }

    return { time: { timezone: currentTimezone } };
  }, [searchSelection]);

  const searchCategory = useMemo<string | undefined>(() => {
    if (searchSel?.category) {
      return searchSel?.category;
    }

    return undefined;
  }, [searchSel?.category]);

  const onCategoryClick = useCallback(
    (categoryId: string) => {
      if (!isNil(searchCategory) && categoryId === searchCategory) {
        dispatch(
          setSearchSelection({
            ...searchSel,
            category: undefined,
          }),
        );
      } else {
        dispatch(setSearchSelection({ ...searchSel, category: categoryId }));
      }
    },
    [searchCategory, searchSel],
  );

  const activeCatRef = useRef<null | HTMLDivElement>(null);

  useEffect(() => {
    if (activeCatRef?.current) {
      activeCatRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });
    }
  }, [searchCategory]);

  const availableCategories = useMemo(() => {
    if (!configCategories || configCategories.length <= 0) {
      return [];
    }

    return configCategories.map(
      (configCat) =>
        CATEGORY_DESCRIPTORS.filter((c) => c.id === configCat.id)[0],
    );
  }, [configCategories]);

  return (
    <div className="grid min-h-full auto-cols-auto grid-flow-col gap-2 overflow-x-auto px-2 pt-2">
      {availableCategories.map((item, index) => {
        const selected: boolean =
          !isNil(searchCategory) && item.id === searchCategory;

        return (
          <div
            key={item.id}
            ref={selected && !activeCatRef?.current ? activeCatRef : undefined}
          >
            <CategoryBox
              id={item.id}
              data-test-id={`category-bar-item-${item.id}`}
              label={t(`static.categories.${item.id}`)}
              icon={item.icon}
              onClick={() => onCategoryClick(item.id)}
              selected={selected}
            />
          </div>
        );
      })}
    </div>
  );
};

export default Categories;
